'use client';

import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getUnseenAppRelease, acknowledgeAppRelease, type AppRelease } from '@/actions/releases';
import WhatsNewModal from '@/components/WhatsNewModal';

/**
 * Shown once per user per notifiable release (minor/major bumps only — see
 * decisions.md's versioning-policy entry). Mounted once in Providers so it
 * appears for both staff and student surfaces without per-page wiring.
 *
 * Compact by default (the "immediately after login" cue from the two-layer
 * update system) — "See what's new" expands into the full WhatsNewModal
 * (Layer 1). Either dismissal path (the small "Got it" or closing the modal)
 * acknowledges the release so it doesn't nag again.
 */
export default function UpdateAlertBanner() {
  const { user } = useAuth();
  const [release, setRelease] = useState<AppRelease | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    getUnseenAppRelease().then((r) => {
      if (!cancelled) setRelease(r);
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!release || dismissed) return null;

  function acknowledge() {
    if (release) acknowledgeAppRelease(release.version);
  }

  function handleDismiss() {
    acknowledge();
    setDismissed(true);
  }

  function handleModalClose() {
    acknowledge();
    setModalOpen(false);
    setDismissed(true);
  }

  const teaser = release.intro || release.highlights[0]?.body || release.summary;

  return (
    <>
      {!modalOpen && (
        <div
          role="status"
          className="fixed bottom-4 left-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 rounded-lg border shadow-lg px-4 py-3 bg-[var(--bg-surface)] border-[var(--border)]"
        >
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold" style={{ color: 'var(--amber)' }}>
                New update available
              </div>
              <p className="mt-1 text-sm text-[var(--text-1)] line-clamp-2">{teaser}</p>
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                className="mt-1.5 text-xs font-medium hover:underline"
                style={{ color: 'var(--amber)' }}
              >
                See what&rsquo;s new →
              </button>
            </div>
            <button
              type="button"
              onClick={handleDismiss}
              className="shrink-0 text-xs font-medium px-2 py-1 rounded text-[var(--text-3)]"
            >
              Got it
            </button>
          </div>
        </div>
      )}
      {modalOpen && (
        <WhatsNewModal
          headline={release.headline}
          intro={release.intro}
          summary={release.summary}
          highlights={release.highlights}
          changelogHref={`/changelog#v${release.version}`}
          onClose={handleModalClose}
        />
      )}
    </>
  );
}
